import {
  upsertLibraryReceipt,
  validatePrivateSummaryTransition,
  type Bookmark,
  type LibraryRecipientEntry,
  type LibraryReceipt,
  type LibraryStatus,
  toLibraryRecipientEntry,
} from "./domain";
import {
  assertContinuityManifest,
  type ContinuityIdentity,
  type ContinuityManifest,
} from "./continuity";

export const SYNTHETIC_WORKSPACE_KEY = "convoful.synthetic-workspace.v1";
export const SYNTHETIC_CONVERSATION_SESSION_KEY =
  "convoful.synthetic-conversation-session.v1";
export const PRIVATE_CONTINUITY_MANIFEST_KEY =
  "convoful.private-continuity-manifests.v1";
export const SYNTHETIC_MEDIA_DATABASE = "convoful-synthetic-media-v1";
export const SYNTHETIC_MEDIA_REFERENCE_PREFIX = "fixture-media:";
export const SYNTHETIC_CARD_BACK_DATABASE =
  "convoful-synthetic-card-backs-v1";
export const SYNTHETIC_CARD_BACK_REFERENCE_PREFIX = "fixture-card-back:";
export const SYNTHETIC_CARD_BACK_AUDIENCE_KEY_PREFIX = "audience:";
export const SYNTHETIC_CARD_BACK_LEGACY_KEY = "photos";
export const MAX_SYNTHETIC_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_SYNTHETIC_CARD_BACK_PHOTOS = 40;
const SYNTHETIC_CARD_BACK_LEGACY_AUDIENCE_ID = "maya";
export const SYNTHETIC_IMAGE_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "image/avif",
  "image/heic",
  "image/heif",
] as const;

export interface SummaryEnrichmentJob {
  id: string;
  bookmarkId: string;
  summaryFingerprint: string;
  state: "pending" | "queued" | "failed";
}

export interface LocalFixtureSummaryEnrichmentJob
extends SummaryEnrichmentJob {
  transport: "local_fixture";
  queuedAt: string;
  remoteExecuted: false;
}

export interface OwnerBookmarkWorkspace {
  bookmarks: Bookmark[];
  enrichmentJobs: SummaryEnrichmentJob[];
}

export interface BookmarkWorkspace extends OwnerBookmarkWorkspace {
  receipts: LibraryReceipt[];
}

export interface SyntheticBookmarkWorkspace extends BookmarkWorkspace {
  schemaVersion: 1;
  kind: "synthetic_fixture";
  enrichmentJobs: LocalFixtureSummaryEnrichmentJob[];
}

export interface WorkspaceLoadResult {
  workspace: OwnerBookmarkWorkspace;
  context?: OwnerWorkspaceContext;
  recoveredInvalidState?: boolean;
}

export interface OwnerWorkspaceAudience {
  id: string;
  label: string;
}

export interface OwnerWorkspaceContext {
  ownerUserID: string;
  audiences: OwnerWorkspaceAudience[];
}

interface SyntheticWorkspaceLoadResult {
  workspace: SyntheticBookmarkWorkspace;
  recoveredInvalidState: boolean;
}

export interface BookmarkSaveResult {
  workspace: OwnerBookmarkWorkspace;
  summaryEnrichmentJob: SummaryEnrichmentJob | null;
}

export interface BookmarkCreateResult extends BookmarkSaveResult {
  bookmark: Bookmark;
}

export interface BookmarkRepository {
  load(): Promise<WorkspaceLoadResult>;
  saveBookmark(bookmark: Bookmark): Promise<BookmarkSaveResult>;
}

export interface BookmarkCreateRepository {
  createBookmark(bookmark: Bookmark): Promise<BookmarkCreateResult>;
}

export function supportsBookmarkCreate(
  repository: BookmarkRepository,
): repository is BookmarkRepository & BookmarkCreateRepository {
  return "createBookmark" in repository
    && typeof repository.createBookmark === "function";
}

export interface LibraryReceiptRepository {
  loadReceipt(publicationID: string): Promise<LibraryReceipt | null>;
  provisionReceipt(publicationID: string): Promise<LibraryReceipt>;
  transitionReceipt(
    publicationID: string,
    state: LibraryStatus,
  ): Promise<LibraryReceipt>;
}

export interface RecipientLibraryRepository
extends LibraryReceiptRepository {
  loadLibrary(): Promise<LibraryRecipientEntry[]>;
}

export interface ConversationOutcomeRepository {
  saveConversationOutcome(bookmark: Bookmark): Promise<OwnerBookmarkWorkspace>;
}

export type AuthoritativeConversationCommandAction =
  "reveal" | "discussed" | "postponed" | "correct" | "end";

export type AuthoritativeConversationEventAction =
  "revealed" | "discussed" | "postponed" | "corrected" | "ended";

export type AuthoritativeConversationEndReason =
  "completed" | "paused" | "abandoned";

export interface AuthoritativeConversationCard {
  assignmentID: string;
  position: number;
  state: "playable" | "discussed";
  revealed: boolean;
  lastEventID: string | null;
  lastOutcomeEventID: string | null;
}

export interface AuthoritativeConversationEvent {
  id: string;
  assignmentID: string | null;
  action: AuthoritativeConversationEventAction;
  targetEventID: string | null;
  previousPosition: number | null;
  nextPosition: number | null;
  occurredAt: string;
  revision: number;
}

export interface AuthoritativeConversationSession {
  id: string;
  audienceID: string;
  revision: number;
  startedAt: string;
  endedAt: string | null;
  endReason: AuthoritativeConversationEndReason | null;
  updatedAt: string;
  cards: AuthoritativeConversationCard[];
  events: AuthoritativeConversationEvent[];
}

export interface AuthoritativeConversationCleanupPointer {
  id: string;
  revision: number;
}

export interface AuthoritativeConversationSessions {
  revision: number;
  etag: string;
  sessions: AuthoritativeConversationSession[];
  cleanupRequiredSession: AuthoritativeConversationCleanupPointer | null;
}

export interface AuthoritativeConversationMutation {
  revision: number;
  etag: string;
  session: AuthoritativeConversationSession;
  event: AuthoritativeConversationEvent | null;
  card: AuthoritativeConversationCard | null;
}

export interface StartAuthoritativeConversationInput {
  audienceID: string;
  assignmentIDs: string[];
  etag: string;
  idempotencyKey: string;
}

export interface CommandAuthoritativeConversationInput {
  sessionID: string;
  action: AuthoritativeConversationCommandAction;
  assignmentID: string | null;
  targetEventID: string | null;
  endReason: AuthoritativeConversationEndReason | null;
  etag: string;
  idempotencyKey: string;
}

export interface AuthoritativeConversationRepository {
  load(audienceID?: string): Promise<AuthoritativeConversationSessions>;
  start(
    input: StartAuthoritativeConversationInput,
  ): Promise<AuthoritativeConversationMutation>;
  command(
    input: CommandAuthoritativeConversationInput,
  ): Promise<AuthoritativeConversationMutation>;
}

export interface ResettableBookmarkRepository {
  reset(): Promise<BookmarkWorkspace>;
}

export type ConversationSessionAction =
  "revealed" | "discussed" | "postponed";

export type ConversationSessionDateFilter =
  "all" | "week" | "two_weeks" | "month" | "custom";

export type ConversationSessionLayout = "stack" | "hand" | "table" | "all";
export const MAX_SYNTHETIC_ENDED_SESSIONS = 24;

export interface ConversationSessionEventPointer {
  id: string;
  bookmarkID: string;
  audienceId: string;
  action: ConversationSessionAction;
  occurredAt: string;
}

export interface ConversationSessionSnapshot {
  id: string;
  startedAt: string;
  audienceId: string;
  deckOrders: Record<string, string[]>;
  events: ConversationSessionEventPointer[];
  selectedThemes: string[];
  dateFilter: ConversationSessionDateFilter;
  customDateStart: string;
  customDateEnd: string;
  showThemeSymbols: boolean;
  statsVisible: boolean;
  layout: ConversationSessionLayout;
  stackIndex: number;
}

export interface EndedConversationSessionSnapshot {
  id: string;
  startedAt: string;
  endedAt: string;
  events: ConversationSessionEventPointer[];
}

export interface ConversationSessionLoadResult {
  session: ConversationSessionSnapshot | null;
  endedSessions: EndedConversationSessionSnapshot[];
  recoveredInvalidState?: boolean;
}

export interface ConversationSessionRepository {
  load(): Promise<ConversationSessionLoadResult>;
  save(
    session: ConversationSessionSnapshot | null,
    endedSessions: readonly EndedConversationSessionSnapshot[],
  ): Promise<void>;
  clear(): Promise<void>;
}

export interface ContinuityManifestLoadResult {
  manifest: ContinuityManifest | null;
  recoveredInvalidState: boolean;
}

export interface ContinuityManifestRepository {
  load(identity: ContinuityIdentity): Promise<ContinuityManifestLoadResult>;
  loadLatest(
    principalUserID: string,
    audienceID: string,
  ): Promise<ContinuityManifestLoadResult>;
  save(manifest: ContinuityManifest): Promise<void>;
  remove(principalUserID: string, audienceID: string): Promise<void>;
  clear(): Promise<void>;
}

export interface QueueSummaryEnrichmentInput {
  bookmarkId: string;
  summaryFingerprint: string;
}

export interface SummaryEnrichmentService {
  queueSummaryReembedding(
    input: QueueSummaryEnrichmentInput,
  ): Promise<SummaryEnrichmentJob>;
}

export interface StringStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface PrivateMediaRepository {
  save(reference: string, image: Blob): Promise<void>;
  load(reference: string): Promise<Blob | null>;
  delete(reference: string): Promise<void>;
  prune(retainedReferences: ReadonlySet<string>): Promise<void>;
  clear(): Promise<void>;
}

export interface CardBackPhoto {
  reference: string;
  image: Blob;
}

export interface CardBackPhotoRepository {
  load(audienceID: string): Promise<CardBackPhoto[]>;
  add(
    audienceID: string,
    images: readonly Blob[],
  ): Promise<CardBackPhoto[]>;
  remove(
    audienceID: string,
    reference: string,
  ): Promise<CardBackPhoto[]>;
  clear(audienceID: string): Promise<void>;
  clearAll(): Promise<void>;
}

export class MemoryStringStorage implements StringStorage {
  private readonly values = new Map<string, string>();

  getItem(key: string): string | null {
    return this.values.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.values.set(key, value);
  }

  removeItem(key: string): void {
    this.values.delete(key);
  }
}

interface PrivateContinuityManifestEnvelope {
  schemaVersion: 1;
  kind: "private_continuity_manifests";
  principalUserID: string;
  manifests: ContinuityManifest[];
}

export class LocalContinuityManifestRepository
implements ContinuityManifestRepository {
  constructor(
    private readonly storage: StringStorage,
    private readonly storageKey = PRIVATE_CONTINUITY_MANIFEST_KEY,
  ) {}

  async load(
    identity: ContinuityIdentity,
  ): Promise<ContinuityManifestLoadResult> {
    assertContinuityIdentity(identity.principalUserID, identity.audienceID);
    const read = this.readEnvelope(identity.principalUserID);
    if (read.recoveredInvalidState || read.envelope === null) {
      return {
        manifest: null,
        recoveredInvalidState: read.recoveredInvalidState,
      };
    }
    const manifest = read.envelope.manifests.find(({ audienceID }) => (
      audienceID === identity.audienceID
    ));
    if (!manifest || manifest.sessionID !== identity.sessionID) {
      return {
        manifest: null,
        recoveredInvalidState: false,
      };
    }
    return {
      manifest: structuredClone(manifest),
      recoveredInvalidState: false,
    };
  }

  async loadLatest(
    principalUserID: string,
    audienceID: string,
  ): Promise<ContinuityManifestLoadResult> {
    assertContinuityIdentity(principalUserID, audienceID);
    const read = this.readEnvelope(principalUserID);
    if (read.recoveredInvalidState || read.envelope === null) {
      return {
        manifest: null,
        recoveredInvalidState: read.recoveredInvalidState,
      };
    }
    const manifest = read.envelope.manifests.find((candidate) => (
      candidate.audienceID === audienceID
    ));
    return {
      manifest: manifest ? structuredClone(manifest) : null,
      recoveredInvalidState: false,
    };
  }

  async save(manifest: ContinuityManifest): Promise<void> {
    assertContinuityManifest(manifest);
    const read = this.readEnvelope(manifest.principalUserID);
    if (read.recoveredInvalidState) {
      throw new Error(
        "Invalid private continuity state was cleared; start the deck again.",
      );
    }
    const manifests = (read.envelope?.manifests ?? [])
      .filter(({ audienceID }) => audienceID !== manifest.audienceID);
    manifests.push(structuredClone(manifest));
    const envelope: PrivateContinuityManifestEnvelope = {
      schemaVersion: 1,
      kind: "private_continuity_manifests",
      principalUserID: manifest.principalUserID,
      manifests,
    };
    assertPrivateContinuityManifestEnvelope(envelope);
    this.storage.setItem(this.storageKey, JSON.stringify(envelope));
  }

  async remove(
    principalUserID: string,
    audienceID: string,
  ): Promise<void> {
    assertContinuityIdentity(principalUserID, audienceID);
    const read = this.readEnvelope(principalUserID);
    if (read.recoveredInvalidState || read.envelope === null) return;
    const manifests = read.envelope.manifests.filter((manifest) => (
      manifest.audienceID !== audienceID
    ));
    if (manifests.length === 0) {
      this.storage.removeItem(this.storageKey);
      return;
    }
    const envelope: PrivateContinuityManifestEnvelope = {
      ...read.envelope,
      manifests,
    };
    assertPrivateContinuityManifestEnvelope(envelope);
    this.storage.setItem(this.storageKey, JSON.stringify(envelope));
  }

  async clear(): Promise<void> {
    this.storage.removeItem(this.storageKey);
  }

  private readEnvelope(expectedPrincipalUserID: string): {
    envelope: PrivateContinuityManifestEnvelope | null;
    recoveredInvalidState: boolean;
  } {
    try {
      const stored = this.storage.getItem(this.storageKey);
      if (stored === null) {
        return { envelope: null, recoveredInvalidState: false };
      }
      const envelope = JSON.parse(stored) as unknown;
      assertPrivateContinuityManifestEnvelope(envelope);
      if (envelope.principalUserID !== expectedPrincipalUserID) {
        this.storage.removeItem(this.storageKey);
        return { envelope: null, recoveredInvalidState: true };
      }
      return {
        envelope: structuredClone(envelope),
        recoveredInvalidState: false,
      };
    } catch {
      try {
        this.storage.removeItem(this.storageKey);
      } catch {
        // A blocked store is equivalent to no private resumable manifest.
      }
      return { envelope: null, recoveredInvalidState: true };
    }
  }
}

export class MemoryFixturePrivateMediaRepository
implements PrivateMediaRepository {
  private readonly images = new Map<string, Blob>();

  async save(reference: string, image: Blob): Promise<void> {
    assertSyntheticMedia(reference, image);
    this.images.set(reference, image.slice(0, image.size, image.type));
  }

  async load(reference: string): Promise<Blob | null> {
    assertSyntheticMediaReference(reference);
    const image = this.images.get(reference);
    return image ? image.slice(0, image.size, image.type) : null;
  }

  async delete(reference: string): Promise<void> {
    assertSyntheticMediaReference(reference);
    this.images.delete(reference);
  }

  async prune(retainedReferences: ReadonlySet<string>): Promise<void> {
    for (const reference of this.images.keys()) {
      if (!retainedReferences.has(reference)) this.images.delete(reference);
    }
  }

  async clear(): Promise<void> {
    this.images.clear();
  }
}

export class LocalFixturePrivateMediaRepository
implements PrivateMediaRepository {
  private static readonly objectStore = "images";

  constructor(
    private readonly factory: IDBFactory,
    private readonly databaseName = SYNTHETIC_MEDIA_DATABASE,
  ) {}

  async save(reference: string, image: Blob): Promise<void> {
    assertSyntheticMedia(reference, image);
    await this.withStore("readwrite", (store) => store.put(image, reference));
  }

  async load(reference: string): Promise<Blob | null> {
    assertSyntheticMediaReference(reference);
    const image = await this.withStore(
      "readonly",
      (store) => store.get(reference),
    );
    if (image === undefined) return null;
    if (!(image instanceof Blob)) {
      throw new Error("Stored synthetic private media is invalid.");
    }
    assertSyntheticMedia(reference, image);
    return image;
  }

  async delete(reference: string): Promise<void> {
    assertSyntheticMediaReference(reference);
    await this.withStore("readwrite", (store) => store.delete(reference));
  }

  async prune(retainedReferences: ReadonlySet<string>): Promise<void> {
    const keys = await this.withStore(
      "readonly",
      (store) => store.getAllKeys(),
    );
    await Promise.all(keys.flatMap((key) => (
      typeof key === "string" && retainedReferences.has(key)
        ? []
        : [this.withStore("readwrite", (store) => store.delete(key))]
    )));
  }

  async clear(): Promise<void> {
    await this.withStore("readwrite", (store) => store.clear());
  }

  private async withStore<T>(
    mode: IDBTransactionMode,
    request: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const database = await this.open();
    try {
      const transaction = database.transaction(
        LocalFixturePrivateMediaRepository.objectStore,
        mode,
      );
      const completion = indexedDBTransaction(transaction);
      const requestResult = indexedDBRequest(
        request(transaction.objectStore(
          LocalFixturePrivateMediaRepository.objectStore,
        )),
      );
      const [result] = await Promise.all([requestResult, completion]);
      return result;
    } finally {
      database.close();
    }
  }

  private async open(): Promise<IDBDatabase> {
    const request = this.factory.open(this.databaseName, 1);
    request.addEventListener("upgradeneeded", () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(
        LocalFixturePrivateMediaRepository.objectStore,
      )) {
        database.createObjectStore(
          LocalFixturePrivateMediaRepository.objectStore,
        );
      }
    });
    return indexedDBRequest(request);
  }
}

export class MemoryFixtureCardBackPhotoRepository
implements CardBackPhotoRepository {
  private readonly photosByAudience = new Map<string, CardBackPhoto[]>();

  constructor(
    private readonly createID: () => string = () => crypto.randomUUID(),
  ) {}

  async load(audienceID: string): Promise<CardBackPhoto[]> {
    assertCardBackAudienceID(audienceID);
    return cloneCardBackPhotos(
      this.photosByAudience.get(audienceID) ?? [],
    );
  }

  async add(
    audienceID: string,
    images: readonly Blob[],
  ): Promise<CardBackPhoto[]> {
    assertCardBackAudienceID(audienceID);
    const next = appendCardBackPhotos(
      this.photosByAudience.get(audienceID) ?? [],
      images,
      this.createID,
    );
    this.photosByAudience.set(audienceID, cloneCardBackPhotos(next));
    return cloneCardBackPhotos(next);
  }

  async remove(
    audienceID: string,
    reference: string,
  ): Promise<CardBackPhoto[]> {
    assertCardBackAudienceID(audienceID);
    assertSyntheticCardBackReference(reference);
    const next = (
      this.photosByAudience.get(audienceID) ?? []
    ).filter((photo) => photo.reference !== reference);
    this.photosByAudience.set(audienceID, cloneCardBackPhotos(next));
    return cloneCardBackPhotos(next);
  }

  async clear(audienceID: string): Promise<void> {
    assertCardBackAudienceID(audienceID);
    this.photosByAudience.delete(audienceID);
  }

  async clearAll(): Promise<void> {
    this.photosByAudience.clear();
  }
}

export class LocalFixtureCardBackPhotoRepository
implements CardBackPhotoRepository {
  private static readonly objectStore = "library";

  constructor(
    private readonly factory: IDBFactory,
    private readonly databaseName = SYNTHETIC_CARD_BACK_DATABASE,
    private readonly createID: () => string = () => crypto.randomUUID(),
  ) {}

  async load(audienceID: string): Promise<CardBackPhoto[]> {
    assertCardBackAudienceID(audienceID);
    const database = await this.open();
    try {
      const transaction = database.transaction(
        LocalFixtureCardBackPhotoRepository.objectStore,
        "readwrite",
      );
      const completion = indexedDBTransaction(transaction);
      const photos = await this.readScopedPhotos(
        transaction.objectStore(
          LocalFixtureCardBackPhotoRepository.objectStore,
        ),
        audienceID,
      );
      await completion;
      return cloneCardBackPhotos(photos);
    } finally {
      database.close();
    }
  }

  async add(
    audienceID: string,
    images: readonly Blob[],
  ): Promise<CardBackPhoto[]> {
    return this.update(audienceID, (current) => (
      appendCardBackPhotos(current, images, this.createID)
    ));
  }

  async remove(
    audienceID: string,
    reference: string,
  ): Promise<CardBackPhoto[]> {
    assertSyntheticCardBackReference(reference);
    return this.update(audienceID, (current) => (
      current.filter((photo) => photo.reference !== reference)
    ));
  }

  async clear(audienceID: string): Promise<void> {
    assertCardBackAudienceID(audienceID);
    const database = await this.open();
    try {
      const transaction = database.transaction(
        LocalFixtureCardBackPhotoRepository.objectStore,
        "readwrite",
      );
      const completion = indexedDBTransaction(transaction);
      const store = transaction.objectStore(
        LocalFixtureCardBackPhotoRepository.objectStore,
      );
      await this.readScopedPhotos(store, audienceID);
      store.delete(cardBackAudienceKey(audienceID));
      await completion;
    } finally {
      database.close();
    }
  }

  async clearAll(): Promise<void> {
    const database = await this.open();
    try {
      const transaction = database.transaction(
        LocalFixtureCardBackPhotoRepository.objectStore,
        "readwrite",
      );
      const completion = indexedDBTransaction(transaction);
      transaction
        .objectStore(LocalFixtureCardBackPhotoRepository.objectStore)
        .clear();
      await completion;
    } finally {
      database.close();
    }
  }

  private async update(
    audienceID: string,
    transform: (current: CardBackPhoto[]) => CardBackPhoto[],
  ): Promise<CardBackPhoto[]> {
    assertCardBackAudienceID(audienceID);
    const database = await this.open();
    try {
      const transaction = database.transaction(
        LocalFixtureCardBackPhotoRepository.objectStore,
        "readwrite",
      );
      const store = transaction.objectStore(
        LocalFixtureCardBackPhotoRepository.objectStore,
      );
      const completion = indexedDBTransaction(transaction);
      const current = await this.readScopedPhotos(store, audienceID);
      const next = transform(cloneCardBackPhotos(current));
      assertStoredCardBackPhotos(next);
      store.put(
        cloneCardBackPhotos(next),
        cardBackAudienceKey(audienceID),
      );
      await completion;
      return cloneCardBackPhotos(next);
    } finally {
      database.close();
    }
  }

  private async readScopedPhotos(
    store: IDBObjectStore,
    audienceID: string,
  ): Promise<CardBackPhoto[]> {
    const audienceKey = cardBackAudienceKey(audienceID);
    const mayaKey = cardBackAudienceKey(
      SYNTHETIC_CARD_BACK_LEGACY_AUDIENCE_ID,
    );
    const [stored, legacy, storedMaya] = await Promise.all([
      indexedDBRequest(store.get(audienceKey)),
      indexedDBRequest(store.get(SYNTHETIC_CARD_BACK_LEGACY_KEY)),
      audienceID === SYNTHETIC_CARD_BACK_LEGACY_AUDIENCE_ID
        ? Promise.resolve(undefined)
        : indexedDBRequest(store.get(mayaKey)),
    ]);
    const current = stored === undefined ? [] : stored;
    assertStoredCardBackPhotos(current);
    if (legacy === undefined) return cloneCardBackPhotos(current);

    assertStoredCardBackPhotos(legacy);
    const currentMaya = audienceID === SYNTHETIC_CARD_BACK_LEGACY_AUDIENCE_ID
      ? current
      : storedMaya === undefined
        ? []
        : storedMaya;
    assertStoredCardBackPhotos(currentMaya);
    const migratedMaya = mergeCardBackPhotos(currentMaya, legacy);
    store.put(cloneCardBackPhotos(migratedMaya), mayaKey);
    store.delete(SYNTHETIC_CARD_BACK_LEGACY_KEY);
    return audienceID === SYNTHETIC_CARD_BACK_LEGACY_AUDIENCE_ID
      ? cloneCardBackPhotos(migratedMaya)
      : cloneCardBackPhotos(current);
  }

  private async open(): Promise<IDBDatabase> {
    const request = this.factory.open(this.databaseName, 1);
    request.addEventListener("upgradeneeded", () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(
        LocalFixtureCardBackPhotoRepository.objectStore,
      )) {
        database.createObjectStore(
          LocalFixtureCardBackPhotoRepository.objectStore,
        );
      }
    });
    return indexedDBRequest(request);
  }
}

export class LocalFixtureBookmarkRepository
implements
  BookmarkRepository,
  BookmarkCreateRepository,
  RecipientLibraryRepository,
  ConversationOutcomeRepository,
  ResettableBookmarkRepository {
  private readonly enrichmentService: SummaryEnrichmentService;
  private readonly storageKey: string;
  private readonly receiptUserID: string;
  private readonly now: () => string;

  constructor(
    private readonly storage: StringStorage,
    private readonly seed: SyntheticBookmarkWorkspace,
    options: {
      receiptUserID: string;
      storageKey?: string;
      now?: () => string;
    },
    enrichmentService: SummaryEnrichmentService =
      new LocalFixtureSummaryEnrichmentService(),
  ) {
    assertSyntheticWorkspace(seed);
    if (!options.receiptUserID.trim()) {
      throw new Error("A synthetic receipt viewer is required.");
    }
    this.receiptUserID = options.receiptUserID;
    this.storageKey = options.storageKey ?? SYNTHETIC_WORKSPACE_KEY;
    this.now = options.now ?? (() => new Date().toISOString());
    this.enrichmentService = enrichmentService;
  }

  async load(): Promise<WorkspaceLoadResult> {
    const result = await this.loadSyntheticWorkspace();
    return {
      workspace: ownerBookmarkWorkspace(result.workspace),
      recoveredInvalidState: result.recoveredInvalidState,
    };
  }

  async loadReceipt(publicationID: string): Promise<LibraryReceipt | null> {
    const { workspace } = await this.loadSyntheticWorkspace();
    assertLiveLibraryPublication(workspace, publicationID);
    const matching = workspace.receipts.filter((receipt) => (
      receipt.publicationId === publicationID
      && receipt.userId === this.receiptUserID
    ));
    if (matching.length > 1) {
      throw new Error("The synthetic Library receipt is duplicated.");
    }
    return matching[0] ? structuredClone(matching[0]) : null;
  }

  async loadLibrary(): Promise<LibraryRecipientEntry[]> {
    const { workspace } = await this.loadSyntheticWorkspace();
    return workspace.bookmarks.flatMap((bookmark) => (
      bookmark.audienceAssignments.flatMap((assignment) => {
        const entry = toLibraryRecipientEntry(
          bookmark,
          assignment.audienceId,
          this.receiptUserID,
          workspace.receipts,
        );
        return entry ? [structuredClone(entry)] : [];
      })
    ));
  }

  private async loadSyntheticWorkspace(): Promise<SyntheticWorkspaceLoadResult> {
    const stored = this.storage.getItem(this.storageKey);
    if (stored === null) {
      const workspace = cloneWorkspace(this.seed);
      this.persist(workspace);
      return { workspace, recoveredInvalidState: false };
    }
    try {
      const workspace = JSON.parse(stored) as unknown;
      assertSyntheticWorkspace(workspace);
      return {
        workspace: cloneWorkspace(workspace),
        recoveredInvalidState: false,
      };
    } catch {
      const workspace = cloneWorkspace(this.seed);
      this.storage.removeItem(this.storageKey);
      this.persist(workspace);
      return { workspace, recoveredInvalidState: true };
    }
  }

  async saveBookmark(bookmark: Bookmark): Promise<BookmarkSaveResult> {
    assertSyntheticBookmarkID(bookmark.id);
    const { workspace } = await this.loadSyntheticWorkspace();
    const bookmarkIndex = workspace.bookmarks.findIndex(
      ({ id }) => id === bookmark.id,
    );
    const previousSummary = bookmarkIndex < 0
      ? ""
      : workspace.bookmarks[bookmarkIndex].privateHumanSummary;
    const summaryError = validatePrivateSummaryTransition(
      previousSummary,
      bookmark.privateHumanSummary,
    );
    if (summaryError) throw new Error(summaryError);
    const summaryChanged = (
      previousSummary.trim() !== bookmark.privateHumanSummary.trim()
    );
    const enrichmentJob = summaryChanged && bookmark.privateHumanSummary.trim()
      ? await this.enrichmentService.queueSummaryReembedding({
        bookmarkId: bookmark.id,
        summaryFingerprint: bookmark.summaryFingerprint,
      })
      : null;
    if (enrichmentJob) assertSyntheticEnrichmentJob(enrichmentJob);

    if (bookmarkIndex < 0) {
      workspace.bookmarks.push(structuredClone(bookmark));
    } else {
      workspace.bookmarks[bookmarkIndex] = structuredClone(bookmark);
    }
    if (enrichmentJob) {
      assertSyntheticEnrichmentJob(enrichmentJob);
      workspace.enrichmentJobs = [
        ...workspace.enrichmentJobs.filter(
          ({ bookmarkId }) => bookmarkId !== bookmark.id,
        ),
        structuredClone(enrichmentJob),
      ];
    }
    this.persist(workspace);
    return {
      workspace: ownerBookmarkWorkspace(workspace),
      summaryEnrichmentJob: enrichmentJob
        ? structuredClone(enrichmentJob)
        : null,
    };
  }

  async createBookmark(bookmark: Bookmark): Promise<BookmarkCreateResult> {
    assertSyntheticBookmarkID(bookmark.id);
    const { workspace } = await this.loadSyntheticWorkspace();
    const existing = workspace.bookmarks.find(({ id }) => id === bookmark.id);
    if (existing) {
      if (JSON.stringify(existing) !== JSON.stringify(bookmark)) {
        throw new Error(
          "This synthetic New Card identity already belongs to another bookmark.",
        );
      }
      const enrichmentJob = workspace.enrichmentJobs.find(
        ({ bookmarkId }) => bookmarkId === bookmark.id,
      ) ?? null;
      return {
        workspace: ownerBookmarkWorkspace(workspace),
        bookmark: structuredClone(existing),
        summaryEnrichmentJob: enrichmentJob
          ? structuredClone(enrichmentJob)
          : null,
      };
    }
    const result = await this.saveBookmark(bookmark);
    const created = result.workspace.bookmarks.find(
      ({ id }) => id === bookmark.id,
    );
    if (!created) {
      throw new Error("The synthetic New Card was not returned after saving.");
    }
    return {
      ...result,
      bookmark: structuredClone(created),
    };
  }

  async provisionReceipt(
    publicationID: string,
  ): Promise<LibraryReceipt> {
    const { workspace } = await this.loadSyntheticWorkspace();
    assertLiveLibraryPublication(workspace, publicationID);
    const matching = workspace.receipts.filter((receipt) => (
      receipt.publicationId === publicationID
      && receipt.userId === this.receiptUserID
    ));
    if (matching.length > 1) {
      throw new Error("The synthetic Library receipt is duplicated.");
    }
    if (matching.length === 1) {
      return structuredClone(matching[0]);
    }
    workspace.receipts = upsertLibraryReceipt(
      workspace.receipts,
      publicationID,
      this.receiptUserID,
      "new",
      this.now(),
    );
    this.persist(workspace);
    return persistedReceipt(workspace, publicationID, this.receiptUserID);
  }

  async transitionReceipt(
    publicationID: string,
    state: LibraryStatus,
  ): Promise<LibraryReceipt> {
    const { workspace } = await this.loadSyntheticWorkspace();
    assertLiveLibraryPublication(workspace, publicationID);
    const matching = workspace.receipts.filter((receipt) => (
      receipt.publicationId === publicationID
      && receipt.userId === this.receiptUserID
    ));
    if (matching.length !== 1) {
      throw new Error(
        matching.length > 1
          ? "The synthetic Library receipt is duplicated."
          : "The synthetic Library receipt has not been provisioned.",
      );
    }
    workspace.receipts = upsertLibraryReceipt(
      workspace.receipts,
      publicationID,
      this.receiptUserID,
      state,
      this.now(),
    );
    this.persist(workspace);
    return persistedReceipt(workspace, publicationID, this.receiptUserID);
  }

  async saveConversationOutcome(
    bookmark: Bookmark,
  ): Promise<OwnerBookmarkWorkspace> {
    assertSyntheticBookmarkID(bookmark.id);
    const { workspace } = await this.loadSyntheticWorkspace();
    const bookmarkIndex = workspace.bookmarks.findIndex(
      ({ id }) => id === bookmark.id,
    );
    if (bookmarkIndex < 0) {
      throw new Error("Cannot persist an outcome for an unknown synthetic bookmark.");
    }
    workspace.bookmarks[bookmarkIndex] = structuredClone(bookmark);
    this.persist(workspace);
    return ownerBookmarkWorkspace(workspace);
  }

  async reset(): Promise<BookmarkWorkspace> {
    this.storage.removeItem(this.storageKey);
    const workspace = cloneWorkspace(this.seed);
    this.persist(workspace);
    return workspace;
  }

  private persist(workspace: SyntheticBookmarkWorkspace): void {
    assertSyntheticWorkspace(workspace);
    this.storage.setItem(this.storageKey, JSON.stringify(workspace));
  }
}

export class LocalFixtureSummaryEnrichmentService
implements SummaryEnrichmentService {
  constructor(
    private readonly now: () => string = () => new Date().toISOString(),
    private readonly createID: () => string = () => crypto.randomUUID(),
  ) {}

  async queueSummaryReembedding(
    input: QueueSummaryEnrichmentInput,
  ): Promise<LocalFixtureSummaryEnrichmentJob> {
    assertSyntheticBookmarkID(input.bookmarkId);
    if (!input.summaryFingerprint.trim()) {
      throw new Error("A Summary fingerprint is required before queueing.");
    }
    return {
      id: `local-reindex-${this.createID()}`,
      bookmarkId: input.bookmarkId,
      summaryFingerprint: input.summaryFingerprint,
      state: "queued",
      transport: "local_fixture",
      queuedAt: this.now(),
      remoteExecuted: false,
    };
  }
}

interface SyntheticConversationSessionEnvelopeV1 {
  schemaVersion: 1;
  kind: "synthetic_conversation_session";
  session: ConversationSessionSnapshot;
}

interface SyntheticConversationSessionEnvelope {
  schemaVersion: 2;
  kind: "synthetic_conversation_session";
  session: ConversationSessionSnapshot | null;
  endedSessions: EndedConversationSessionSnapshot[];
}

function normalizeSyntheticConversationSessionEnvelope(
  value: unknown,
): unknown {
  if (
    typeof value === "object"
    && value !== null
    && hasOnlyKeys(value, ["schemaVersion", "kind", "session"])
    && (value as { schemaVersion?: unknown }).schemaVersion === 1
    && (value as { kind?: unknown }).kind === "synthetic_conversation_session"
  ) {
    const prior = value as SyntheticConversationSessionEnvelopeV1;
    return {
      schemaVersion: 2,
      kind: prior.kind,
      session: normalizeConversationSessionSnapshot(prior.session),
      endedSessions: [],
    };
  }
  if (
    typeof value !== "object"
    || value === null
    || !("session" in value)
  ) {
    return value;
  }
  const envelope = value as Record<string, unknown>;
  return {
    ...envelope,
    session: envelope.session === null
      ? null
      : normalizeConversationSessionSnapshot(envelope.session),
  };
}

function normalizeConversationSessionSnapshot(value: unknown): unknown {
  if (
    typeof value !== "object"
    || value === null
    || "statsVisible" in value
  ) {
    return value;
  }
  return {
    ...value,
    statsVisible: true,
  };
}

export class LocalFixtureConversationSessionRepository
implements ConversationSessionRepository {
  constructor(
    private readonly storage: StringStorage,
    private readonly storageKey = SYNTHETIC_CONVERSATION_SESSION_KEY,
  ) {}

  async load(): Promise<ConversationSessionLoadResult> {
    try {
      const stored = this.storage.getItem(this.storageKey);
      if (stored === null) {
        return {
          session: null,
          endedSessions: [],
          recoveredInvalidState: false,
        };
      }
      const envelope = normalizeSyntheticConversationSessionEnvelope(
        JSON.parse(stored) as unknown,
      );
      assertSyntheticConversationSessionEnvelope(envelope);
      return {
        session: envelope.session
          ? structuredClone(envelope.session)
          : null,
        endedSessions: structuredClone(envelope.endedSessions),
        recoveredInvalidState: false,
      };
    } catch {
      try {
        this.storage.removeItem(this.storageKey);
      } catch {
        // A blocked tab store is equivalent to no resumable fixture session.
      }
      return {
        session: null,
        endedSessions: [],
        recoveredInvalidState: true,
      };
    }
  }

  async save(
    session: ConversationSessionSnapshot | null,
    endedSessions: readonly EndedConversationSessionSnapshot[],
  ): Promise<void> {
    const envelope: SyntheticConversationSessionEnvelope = {
      schemaVersion: 2,
      kind: "synthetic_conversation_session",
      session: session ? structuredClone(session) : null,
      endedSessions: structuredClone([...endedSessions]),
    };
    assertSyntheticConversationSessionEnvelope(envelope);
    this.storage.setItem(this.storageKey, JSON.stringify(envelope));
  }

  async clear(): Promise<void> {
    this.storage.removeItem(this.storageKey);
  }
}

export function syntheticWorkspace(
  bookmarks: Bookmark[],
  receipts: LibraryReceipt[],
): SyntheticBookmarkWorkspace {
  return {
    schemaVersion: 1,
    kind: "synthetic_fixture",
    bookmarks: structuredClone(bookmarks),
    receipts: structuredClone(receipts),
    enrichmentJobs: [],
  };
}

function cloneWorkspace<T extends BookmarkWorkspace>(workspace: T): T {
  return structuredClone(workspace);
}

function ownerBookmarkWorkspace(
  workspace: BookmarkWorkspace,
): OwnerBookmarkWorkspace {
  return {
    bookmarks: structuredClone(workspace.bookmarks),
    enrichmentJobs: structuredClone(workspace.enrichmentJobs),
  };
}

function assertLiveLibraryPublication(
  workspace: BookmarkWorkspace,
  publicationID: string,
): void {
  if (!publicationID.trim() || publicationID !== publicationID.trim()) {
    throw new Error("A trimmed Library publication ID is required.");
  }
  const matches = workspace.bookmarks.flatMap((bookmark) => (
    bookmark.libraryPublications
      .filter(({ id }) => id === publicationID)
      .map((publication) => ({ bookmark, publication }))
  ));
  if (matches.length !== 1) {
    throw new Error("The synthetic Library publication is unavailable.");
  }
  const [{ bookmark, publication }] = matches;
  const assignment = bookmark.audienceAssignments.find(
    ({ id }) => id === publication.assignmentId,
  );
  if (
    publication.revokedAt !== null
    || !assignment
    || !assignment.libraryEnabled
  ) {
    throw new Error("The synthetic Library publication is unavailable.");
  }
}

function persistedReceipt(
  workspace: BookmarkWorkspace,
  publicationID: string,
  userID: string,
): LibraryReceipt {
  const receipt = workspace.receipts.find((candidate) => (
    candidate.publicationId === publicationID
    && candidate.userId === userID
  ));
  if (!receipt) {
    throw new Error("The synthetic Library receipt was not persisted.");
  }
  return structuredClone(receipt);
}

function assertSyntheticWorkspace(
  value: unknown,
): asserts value is SyntheticBookmarkWorkspace {
  if (
    typeof value !== "object"
    || value === null
    || (value as Partial<SyntheticBookmarkWorkspace>).schemaVersion !== 1
    || (value as Partial<SyntheticBookmarkWorkspace>).kind !== "synthetic_fixture"
    || !Array.isArray((value as Partial<BookmarkWorkspace>).bookmarks)
    || !Array.isArray((value as Partial<BookmarkWorkspace>).receipts)
    || !Array.isArray((value as Partial<BookmarkWorkspace>).enrichmentJobs)
  ) {
    throw new Error("Stored synthetic workspace has an invalid envelope.");
  }
  const workspace = value as BookmarkWorkspace;
  workspace.bookmarks.forEach(({ id }) => assertSyntheticBookmarkID(id));
  workspace.enrichmentJobs.forEach(assertSyntheticEnrichmentJob);
}

function assertSyntheticBookmarkID(bookmarkID: string): void {
  if (!/^(demo|local)-[a-z0-9-]+$/i.test(bookmarkID)) {
    throw new Error(
      "The local fixture repository accepts only synthetic bookmark IDs.",
    );
  }
}

function assertSyntheticEnrichmentJob(
  job: SummaryEnrichmentJob,
): asserts job is LocalFixtureSummaryEnrichmentJob {
  const candidate = job as Partial<LocalFixtureSummaryEnrichmentJob>;
  assertSyntheticBookmarkID(job.bookmarkId);
  if (
    job.state !== "queued"
    || candidate.transport !== "local_fixture"
    || candidate.remoteExecuted !== false
    || !job.summaryFingerprint.trim()
    || !job.id.startsWith("local-reindex-")
    || typeof candidate.queuedAt !== "string"
    || !candidate.queuedAt
  ) {
    throw new Error("Stored synthetic enrichment job is invalid.");
  }
}

function assertSyntheticConversationSessionEnvelope(
  value: unknown,
): asserts value is SyntheticConversationSessionEnvelope {
  if (
    typeof value !== "object"
    || value === null
    || !hasOnlyKeys(value, [
      "schemaVersion",
      "kind",
      "session",
      "endedSessions",
    ])
    || (value as Partial<SyntheticConversationSessionEnvelope>)
      .schemaVersion !== 2
    || (value as Partial<SyntheticConversationSessionEnvelope>)
      .kind !== "synthetic_conversation_session"
    || !Array.isArray(
      (value as Partial<SyntheticConversationSessionEnvelope>).endedSessions,
    )
  ) {
    throw new Error("Stored synthetic Conversation session has an invalid envelope.");
  }
  const envelope = value as SyntheticConversationSessionEnvelope;
  if (envelope.session !== null) {
    assertSyntheticConversationSession(envelope.session);
  }
  if (
    envelope.endedSessions.length > MAX_SYNTHETIC_ENDED_SESSIONS
    || envelope.endedSessions.some((session) => {
      try {
        assertSyntheticEndedConversationSession(session);
        return false;
      } catch {
        return true;
      }
    })
    || envelope.endedSessions.reduce(
      (total, session) => total + session.events.length,
      envelope.session?.events.length ?? 0,
    ) > 5_000
  ) {
    throw new Error(
      "Stored synthetic Conversation session contains invalid history.",
    );
  }
  const sessionIDs = [
    ...(envelope.session ? [envelope.session.id] : []),
    ...envelope.endedSessions.map(({ id }) => id),
  ];
  if (new Set(sessionIDs).size !== sessionIDs.length) {
    throw new Error(
      "Stored synthetic Conversation session contains duplicate session IDs.",
    );
  }
}

function assertSyntheticConversationSession(
  session: ConversationSessionSnapshot,
): void {
  if (
    typeof session !== "object"
    || session === null
    || !hasOnlyKeys(session, [
      "id",
      "startedAt",
      "audienceId",
      "deckOrders",
      "events",
      "selectedThemes",
      "dateFilter",
      "customDateStart",
      "customDateEnd",
      "showThemeSymbols",
      "statsVisible",
      "layout",
      "stackIndex",
    ])
    || !isBoundedIdentifier(session.id)
    || !isValidTimestamp(session.startedAt)
    || !isEmptyOrBoundedIdentifier(session.audienceId)
    || typeof session.deckOrders !== "object"
    || session.deckOrders === null
    || Array.isArray(session.deckOrders)
    || !Array.isArray(session.events)
    || !Array.isArray(session.selectedThemes)
    || !isConversationDateFilter(session.dateFilter)
    || !isDateInput(session.customDateStart)
    || !isDateInput(session.customDateEnd)
    || typeof session.showThemeSymbols !== "boolean"
    || typeof session.statsVisible !== "boolean"
    || !isConversationLayout(session.layout)
    || !Number.isSafeInteger(session.stackIndex)
    || session.stackIndex < 0
  ) {
    throw new Error("Stored synthetic Conversation session is invalid.");
  }
  const deckEntries = Object.entries(session.deckOrders);
  if (
    deckEntries.length > 100
    || deckEntries.some(([audienceId, bookmarkIDs]) => (
      !isBoundedIdentifier(audienceId)
      || !Array.isArray(bookmarkIDs)
      || bookmarkIDs.length > 1_000
      || bookmarkIDs.some((bookmarkID) => {
        try {
          assertSyntheticBookmarkID(bookmarkID);
          return false;
        } catch {
          return true;
        }
      })
      || new Set(bookmarkIDs).size !== bookmarkIDs.length
    ))
    || session.events.length > 5_000
    || session.events.some((event) => (
      !isSyntheticConversationEventPointer(event)
    ))
    || new Set(session.events.map(({ id }) => id)).size
      !== session.events.length
    || session.selectedThemes.length > 100
    || session.selectedThemes.some((theme) => (
      typeof theme !== "string"
      || !theme.trim()
      || theme.length > 100
    ))
  ) {
    throw new Error("Stored synthetic Conversation session contains invalid pointers.");
  }
}

function assertSyntheticEndedConversationSession(
  session: EndedConversationSessionSnapshot,
): void {
  if (
    typeof session !== "object"
    || session === null
    || !hasOnlyKeys(session, [
      "id",
      "startedAt",
      "endedAt",
      "events",
    ])
    || !isBoundedIdentifier(session.id)
    || !isValidTimestamp(session.startedAt)
    || !isValidTimestamp(session.endedAt)
    || new Date(session.endedAt) < new Date(session.startedAt)
    || !Array.isArray(session.events)
    || session.events.some((event) => (
      !isSyntheticConversationEventPointer(event)
    ))
    || new Set(session.events.map(({ id }) => id)).size
      !== session.events.length
  ) {
    throw new Error(
      "Stored synthetic ended Conversation session is invalid.",
    );
  }
}

function isSyntheticConversationEventPointer(
  event: unknown,
): event is ConversationSessionEventPointer {
  return (
    typeof event === "object"
    && event !== null
    && hasOnlyKeys(event, [
      "id",
      "bookmarkID",
      "audienceId",
      "action",
      "occurredAt",
    ])
    && isBoundedIdentifier((event as ConversationSessionEventPointer).id)
    && isBoundedIdentifier(
      (event as ConversationSessionEventPointer).audienceId,
    )
    && isConversationAction(
      (event as ConversationSessionEventPointer).action,
    )
    && isValidTimestamp(
      (event as ConversationSessionEventPointer).occurredAt,
    )
    && isSyntheticBookmarkID(
      (event as ConversationSessionEventPointer).bookmarkID,
    )
  );
}

function isSyntheticBookmarkID(bookmarkID: unknown): bookmarkID is string {
  if (typeof bookmarkID !== "string") return false;
  try {
    assertSyntheticBookmarkID(bookmarkID);
    return true;
  } catch {
    return false;
  }
}

export function isSyntheticMediaReference(reference: string): boolean {
  return new RegExp(
    `^${SYNTHETIC_MEDIA_REFERENCE_PREFIX}[a-z0-9][a-z0-9-]{0,127}$`,
    "i",
  ).test(reference);
}

export function isAllowedSyntheticImageType(type: string): boolean {
  return (SYNTHETIC_IMAGE_MIME_TYPES as readonly string[])
    .includes(type.toLowerCase());
}

export function isSyntheticCardBackReference(reference: string): boolean {
  return new RegExp(
    `^${SYNTHETIC_CARD_BACK_REFERENCE_PREFIX}`
    + "[a-z0-9][a-z0-9-]{0,127}$",
    "i",
  ).test(reference);
}

function assertSyntheticMediaReference(reference: string): void {
  if (!isSyntheticMediaReference(reference)) {
    throw new Error(
      "The local fixture media repository accepts only opaque fixture-media references.",
    );
  }
}

function assertSyntheticCardBackReference(reference: string): void {
  if (!isSyntheticCardBackReference(reference)) {
    throw new Error(
      "The local card-back repository accepts only opaque "
      + "fixture-card-back references.",
    );
  }
}

function assertSyntheticMedia(reference: string, image: Blob): void {
  assertSyntheticMediaReference(reference);
  if (
    !isAllowedSyntheticImageType(image.type)
    || image.size <= 0
    || image.size > MAX_SYNTHETIC_IMAGE_BYTES
  ) {
    throw new Error(
      "Synthetic private media must be a nonempty image no larger than 10 MB.",
    );
  }
}

function assertCardBackAudienceID(audienceID: string): void {
  if (!isBoundedIdentifier(audienceID)) {
    throw new Error("A valid audience is required for private card-back photos.");
  }
}

function cardBackAudienceKey(audienceID: string): string {
  assertCardBackAudienceID(audienceID);
  return `${SYNTHETIC_CARD_BACK_AUDIENCE_KEY_PREFIX}${audienceID}`;
}

function mergeCardBackPhotos(
  current: readonly CardBackPhoto[],
  legacy: readonly CardBackPhoto[],
): CardBackPhoto[] {
  assertStoredCardBackPhotos(current);
  assertStoredCardBackPhotos(legacy);
  const merged = cloneCardBackPhotos(current);
  const references = new Set(merged.map(({ reference }) => reference));
  for (const photo of legacy) {
    if (references.has(photo.reference)) continue;
    if (merged.length >= MAX_SYNTHETIC_CARD_BACK_PHOTOS) {
      throw new Error(
        "Legacy Maya card-back photos could not be migrated without data loss.",
      );
    }
    references.add(photo.reference);
    merged.push({
      reference: photo.reference,
      image: photo.image.slice(0, photo.image.size, photo.image.type),
    });
  }
  return merged;
}

function appendCardBackPhotos(
  current: readonly CardBackPhoto[],
  images: readonly Blob[],
  createID: () => string,
): CardBackPhoto[] {
  assertStoredCardBackPhotos(current);
  if (
    images.length === 0
    || current.length + images.length > MAX_SYNTHETIC_CARD_BACK_PHOTOS
  ) {
    throw new Error(
      `Choose between 1 and ${
        MAX_SYNTHETIC_CARD_BACK_PHOTOS - current.length
      } more card-back photos.`,
    );
  }
  images.forEach((image) => assertSyntheticImage(image));
  const references = new Set(current.map(({ reference }) => reference));
  const additions = images.map((image) => {
    let reference = "";
    for (let attempt = 0; attempt < 12; attempt += 1) {
      const candidate = `${SYNTHETIC_CARD_BACK_REFERENCE_PREFIX}${createID()}`;
      if (
        isSyntheticCardBackReference(candidate)
        && !references.has(candidate)
      ) {
        reference = candidate;
        break;
      }
    }
    if (!reference) {
      throw new Error("A unique private card-back reference could not be created.");
    }
    references.add(reference);
    return {
      reference,
      image: image.slice(0, image.size, image.type),
    };
  });
  return [...cloneCardBackPhotos(current), ...additions];
}

function assertStoredCardBackPhotos(
  value: unknown,
): asserts value is CardBackPhoto[] {
  if (
    !Array.isArray(value)
    || value.length > MAX_SYNTHETIC_CARD_BACK_PHOTOS
  ) {
    throw new Error("Stored synthetic card-back photos are invalid.");
  }
  const references = new Set<string>();
  for (const photo of value) {
    if (
      typeof photo !== "object"
      || photo === null
      || !("reference" in photo)
      || typeof photo.reference !== "string"
      || !("image" in photo)
      || !(photo.image instanceof Blob)
      || references.has(photo.reference)
    ) {
      throw new Error("Stored synthetic card-back photos are invalid.");
    }
    assertSyntheticCardBackReference(photo.reference);
    assertSyntheticImage(photo.image);
    references.add(photo.reference);
  }
}

function assertSyntheticImage(image: Blob): void {
  if (
    !isAllowedSyntheticImageType(image.type)
    || image.size <= 0
    || image.size > MAX_SYNTHETIC_IMAGE_BYTES
  ) {
    throw new Error(
      "Synthetic private media must be a nonempty image no larger than 10 MB.",
    );
  }
}

function cloneCardBackPhotos(
  photos: readonly CardBackPhoto[],
): CardBackPhoto[] {
  return photos.map(({ reference, image }) => ({
    reference,
    image: image.slice(0, image.size, image.type),
  }));
}

function indexedDBRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), {
      once: true,
    });
    request.addEventListener("error", () => reject(
      request.error ?? new Error("Synthetic private media storage failed."),
    ), { once: true });
  });
}

function indexedDBTransaction(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener("abort", () => reject(
      transaction.error ?? new Error("Synthetic private media storage aborted."),
    ), { once: true });
    transaction.addEventListener("error", () => reject(
      transaction.error ?? new Error("Synthetic private media storage failed."),
    ), { once: true });
  });
}

function assertPrivateContinuityManifestEnvelope(
  value: unknown,
): asserts value is PrivateContinuityManifestEnvelope {
  if (
    typeof value !== "object"
    || value === null
    || Array.isArray(value)
    || !hasOnlyKeys(value, [
      "schemaVersion",
      "kind",
      "principalUserID",
      "manifests",
    ])
  ) {
    throw new Error("Stored private continuity state has an invalid envelope.");
  }
  const envelope = value as Partial<PrivateContinuityManifestEnvelope>;
  if (
    envelope.schemaVersion !== 1
    || envelope.kind !== "private_continuity_manifests"
    || !isBoundedIdentifier(envelope.principalUserID)
    || !Array.isArray(envelope.manifests)
    || envelope.manifests.length > 100
  ) {
    throw new Error("Stored private continuity state is invalid.");
  }
  const audienceIDs = new Set<string>();
  for (const manifest of envelope.manifests) {
    assertContinuityManifest(manifest);
    if (
      manifest.principalUserID !== envelope.principalUserID
      || audienceIDs.has(manifest.audienceID)
    ) {
      throw new Error("Stored private continuity state is not isolated.");
    }
    audienceIDs.add(manifest.audienceID);
  }
}

function assertContinuityIdentity(
  principalUserID: string,
  audienceID: string,
): void {
  if (
    !isBoundedIdentifier(principalUserID)
    || !isBoundedIdentifier(audienceID)
  ) {
    throw new Error(
      "Private continuity storage requires a principal and audience.",
    );
  }
}

function hasOnlyKeys(
  value: object,
  allowedKeys: string[],
): boolean {
  const allowed = new Set(allowedKeys);
  const keys = Object.keys(value);
  return keys.length === allowed.size
    && keys.every((key) => allowed.has(key));
}

function isBoundedIdentifier(value: unknown): value is string {
  return typeof value === "string"
    && /^[a-z0-9][a-z0-9_-]{0,127}$/i.test(value);
}

function isEmptyOrBoundedIdentifier(value: unknown): value is string {
  return value === "" || isBoundedIdentifier(value);
}

function isValidTimestamp(value: unknown): value is string {
  return typeof value === "string"
    && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)
    && !Number.isNaN(new Date(value).valueOf());
}

function isDateInput(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
}

function isConversationAction(
  value: unknown,
): value is ConversationSessionAction {
  return value === "revealed"
    || value === "discussed"
    || value === "postponed";
}

function isConversationDateFilter(
  value: unknown,
): value is ConversationSessionDateFilter {
  return value === "all"
    || value === "week"
    || value === "two_weeks"
    || value === "month"
    || value === "custom";
}

function isConversationLayout(
  value: unknown,
): value is ConversationSessionLayout {
  return value === "stack"
    || value === "hand"
    || value === "table"
    || value === "all";
}
